import React, { useState, useEffect } from 'react';
import importacaoService from '../services/importacaoService';
import { useFinanceiroStore } from '../store/financeiroStore'; 
import { FiPercent, FiClock, FiX, FiActivity, FiDollarSign, FiCreditCard } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { handleApiError } from '../services/errorHandler';

const DesembaracoModal = ({ isOpen, onClose, importacao, onDesembaracoRegistrado }) => {
    const { executarSimulacao, resultadoSimulacao, limparSimulacao, isSimulando } = useFinanceiroStore();

    const [valorFinal, setValorFinal] = useState('');
    const [taxaCambioDesembaraco, setTaxaCambioDesembaraco] = useState('');
    const [dataDesembaraco, setDataDesembaraco] = useState('');
    const [real70Restante, setReal70Restante] = useState('');
    const [real50Desembaraco, setReal50Desembaraco] = useState('');

    const [possuiFinimp30, setPossuiFinimp30] = useState(false);
    const [bancoFinimp30, setBancoFinimp30] = useState('');
    const [dataContratacaoFinimp30, setDataContratacaoFinimp30] = useState('');
    const [taxaJurosMensal30, setTaxaJurosMensal30] = useState('');
    const [prazoFinimp30Dias, setPrazoFinimp30Dias] = useState('');

    const [possuiFinimp70, setPossuiFinimp70] = useState(false);
    const [bancoFinimp70, setBancoFinimp70] = useState('');
    const [dataContratacaoFinimp70, setDataContratacaoFinimp70] = useState('');
    const [taxaJurosMensal70, setTaxaJurosMensal70] = useState('');
    const [prazoFinimp70Dias, setPrazoFinimp70Dias] = useState('');

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    // FUNÇÕES AUXILIARES
    const formatarParaInput = (data) => {
        if (!data) return '';
        try {
            const d = new Date(data);
            if (isNaN(d.getTime())) return '';
            return d.toISOString().split('T')[0];
        } catch (e) {
            return '';
        }
    };

    const parseToNum = (val) => {
        if (val === null || val === undefined || val === '') return null;
        if (typeof val === 'number') return val;
        
        let texto = String(val).trim();
        texto = texto.replace(/[^\d.,-]/g, '');
        
        if (texto.includes('.') && texto.includes(',')) {
            texto = texto.replace(/\./g, ''); 
            texto = texto.replace(',', '.');  
        } else if (texto.includes(',')) {
            texto = texto.replace(',', '.');
        }
        
        const num = Number(texto);
        return isNaN(num) ? 0 : num;
    };

    // Sincroniza dados da importação ao abrir a modal
    useEffect(() => {
        if (importacao && isOpen) {
            setValorFinal(importacao.valorInicial?.toString() || '');
            setTaxaCambioDesembaraco(importacao.taxaCambioEntrada?.toString() || '');
            setReal70Restante(importacao.projecao70Restante?.toString() || '');
            setReal50Desembaraco(importacao.projecao50Desembaraco?.toString() || '');
            
            if (importacao.dataDesembaraco) {
                setDataDesembaraco(formatarParaInput(importacao.dataDesembaraco));
            } else if (importacao.dataImportacao) {
                const dataEmb = new Date(importacao.dataImportacao);
                if (!isNaN(dataEmb.getTime())) {
                    dataEmb.setDate(dataEmb.getDate() + 100);
                    setDataDesembaraco(dataEmb.toISOString().split('T')[0]);
                } else {
                    setDataDesembaraco(new Date().toISOString().split('T')[0]);
                }
            } else {
                setDataDesembaraco(new Date().toISOString().split('T')[0]);
            }
            
            // Campos FINIMP 30%
            setPossuiFinimp30(importacao.possuiFinimp30 || importacao.possuiFinimpInicial || false);
            setBancoFinimp30(importacao.bancoFinimp30 || importacao.bancoFinimpInicial || '');
            setDataContratacaoFinimp30(formatarParaInput(importacao.dataContratacaoFinimp30 || importacao.dataContratacaoFinimpInicial));
            setTaxaJurosMensal30(importacao.taxaJurosMensal30?.toString() || importacao.taxaJurosMensalInicial?.toString() || '');
            setPrazoFinimp30Dias(importacao.prazoFinimp30Dias?.toString() || importacao.prazoMesesInicial?.toString() || '');

            // Campos FINIMP 70%
            setPossuiFinimp70(importacao.possuiFinimp70 || false);
            setBancoFinimp70(importacao.bancoFinimp70 || '');
            setDataContratacaoFinimp70(formatarParaInput(importacao.dataContratacaoFinimp70));
            setTaxaJurosMensal70(importacao.taxaJurosMensal70?.toString() || '');
            setPrazoFinimp70Dias(importacao.prazoFinimp70Dias?.toString() || '');
            
            setError('');
        }
    }, [importacao, isOpen]);

    // Atualiza automáticos (70% e 50%)
    useEffect(() => {
        const nfob = parseToNum(valorFinal);
        const ncambio = parseToNum(taxaCambioDesembaraco);
        if (nfob > 0 && ncambio > 0) {
            const fobTotalReais = nfob * ncambio;
            // Só atualiza projetado na tela
            setReal70Restante((fobTotalReais * 0.70).toFixed(2));
            setReal50Desembaraco((fobTotalReais * 0.50).toFixed(2));
        }
    }, [valorFinal, taxaCambioDesembaraco]);

    // Simulação do Finimp via Store Global (englobando 30 e 70)
    useEffect(() => {
        const nfob = parseToNum(valorFinal);
        const ncambio = parseToNum(taxaCambioDesembaraco);
        const njuros30 = parseToNum(taxaJurosMensal30);
        const nprazo30 = parseToNum(prazoFinimp30Dias);
        const njuros70 = parseToNum(taxaJurosMensal70);
        const nprazo70 = parseToNum(prazoFinimp70Dias);

        if (nfob > 0 && ncambio > 0) {
            executarSimulacao({
                valorFobUs: nfob,
                cotacaoManual: ncambio,
                possuiFinimp30: possuiFinimp30,
                taxaJurosMensal30: njuros30,
                prazoFinimp30Dias: nprazo30,
                possuiFinimp70: possuiFinimp70,
                taxaJurosMensal70: njuros70,
                prazoFinimp70Dias: nprazo70
            });
        } else {
            limparSimulacao();
        }
    }, [valorFinal, taxaCambioDesembaraco, taxaJurosMensal30, prazoFinimp30Dias, possuiFinimp30, taxaJurosMensal70, prazoFinimp70Dias, possuiFinimp70, executarSimulacao, limparSimulacao]);

    // --- LÓGICA BLINDADA DE CÁLCULO (Juros Compostos Locais) ---
    const calcularJurosEstimados = () => {
        // 1. Garante que os valores bases são números (em Reais)
        const principal30 = Number(importacao?.valorPago30) || 0;
        const principal70 = parseToNum(real70Restante) || 0; 
        
        let juros30 = 0;
        if (possuiFinimp30) {
            const taxa = parseToNum(taxaJurosMensal30) || 0;
            const prazoDias = parseToNum(prazoFinimp30Dias) || 0;
            if (taxa > 0 && prazoDias > 0) {
                const i = taxa / 100; // Converte % para decimal
                const prazoM = prazoDias / 30.0;
                const montante = principal30 * Math.pow(1 + i, prazoM);
                juros30 = montante - principal30;
            }
        }
        
        let juros70 = 0;
        if (possuiFinimp70) {
            const taxa = parseToNum(taxaJurosMensal70) || 0;
            const prazoDias = parseToNum(prazoFinimp70Dias) || 0;
            if (taxa > 0 && prazoDias > 0) {
                const i = taxa / 100;
                const prazoM = prazoDias / 30.0;
                const montante = principal70 * Math.pow(1 + i, prazoM);
                juros70 = montante - principal70;
            }
        }
        
        return juros30 + juros70;
    };

    const totalJurosEstimados = calcularJurosEstimados();

    const handleClose = () => {
        if (isSubmitting) return;
        limparSimulacao();
        onClose();
    };

    const handleConfirm = async () => {
        setError('');

        if (possuiFinimp30) {
            const nTaxa = parseToNum(taxaJurosMensal30);
            const nPrazo = parseToNum(prazoFinimp30Dias);
            if (nTaxa <= 0 || nPrazo <= 0) {
                const erroMsg = "Para manter o Finimp de Entrada, a Taxa e o Prazo devem ser informados.";
                setError(erroMsg);
                toast.error(erroMsg);
                return;
            }
        }

        if (possuiFinimp70) {
            const nTaxa = parseToNum(taxaJurosMensal70);
            const nPrazo = parseToNum(prazoFinimp70Dias);
            if (nTaxa <= 0 || nPrazo <= 0) {
                const erroMsg = "Para aplicar o Finimp de Saldo, a Taxa e o Prazo devem ser informados e maiores que zero.";
                setError(erroMsg);
                toast.error(erroMsg);
                return;
            }
        }

        if (isSimulando) {
            toast.error("Aguarde o cálculo dos juros...");
            return;
        }

        let valorFinimpCalculado = 0;
        let metaFaturamentoCalculada = 0;

        if (possuiFinimp30 || possuiFinimp70) {
            const currentState = useFinanceiroStore.getState();
            if (currentState.resultadoSimulacao) {
                valorFinimpCalculado = currentState.resultadoSimulacao.jurosTotal ?? currentState.resultadoSimulacao.valorExibicao ?? 0;
                metaFaturamentoCalculada = currentState.resultadoSimulacao.metaExibicao ?? 0;
            } 
            
            if (valorFinimpCalculado === 0) {
                // FALLBACK LOCAL DUPLO COM MATEMÁTICA PURA DA UI
                valorFinimpCalculado = totalJurosEstimados;
            }
        }

        setIsSubmitting(true);
        try {
            const payload = {
                valorFinal: parseToNum(valorFinal),
                taxaCambioDesembaraco: parseToNum(taxaCambioDesembaraco),
                dataDesembaraco,
                real70Restante: parseToNum(real70Restante),
                real50Desembaraco: parseToNum(real50Desembaraco),
                
                possuiFinimp30: possuiFinimp30, 
                bancoFinimp30: bancoFinimp30 || null,
                dataContratacaoFinimp30: dataContratacaoFinimp30 || null,
                taxaJurosMensal30: possuiFinimp30 ? parseToNum(taxaJurosMensal30) : 0, 
                prazoFinimp30Dias: possuiFinimp30 ? parseToNum(prazoFinimp30Dias) : 0, 

                possuiFinimp70: possuiFinimp70, 
                bancoFinimp70: bancoFinimp70 || null,
                dataContratacaoFinimp70: dataContratacaoFinimp70 || null,
                taxaJurosMensal70: possuiFinimp70 ? parseToNum(taxaJurosMensal70) : 0, 
                prazoFinimp70Dias: possuiFinimp70 ? parseToNum(prazoFinimp70Dias) : 0, 
                
                valorFinimp: (possuiFinimp30 || possuiFinimp70) ? Number(valorFinimpCalculado.toFixed(2)) : 0,
                metaFaturamento: (possuiFinimp30 || possuiFinimp70) && metaFaturamentoCalculada > 0 ? Number(metaFaturamentoCalculada.toFixed(2)) : 0
            };

            await importacaoService.registrarDesembaraco(importacao.id, payload);
            toast.success('Desembaraço concluído e histórico de financiamentos atualizado!');
            onDesembaracoRegistrado();
            handleClose();
        } catch (err) {
            handleApiError(err);
            setError('Erro ao salvar os dados. Verifique os valores.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen || !importacao) return null;

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex justify-center items-center p-4 backdrop-blur-sm pt-10 pb-10">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-300">
                <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-b shrink-0">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                        <FiActivity className="text-blue-600" /> Registrar Desembaraço
                    </h3>
                    <button onClick={handleClose} className="text-gray-400 hover:text-gray-600"><FiX size={24} /></button>
                </div>

                <div className="p-6 space-y-6 flex-1 overflow-y-auto">
                    {error && (
                        <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded text-red-700 text-sm font-medium">
                            {error}
                        </div>
                    )}

                    {/* BLOCO 1: Custos Fechados */}
                    <div className="bg-gray-50/80 p-5 rounded-xl border border-gray-200">
                        <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                            <FiDollarSign className="text-blue-500" /> 1. Custos Fechados do Desembaraço
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5 text-sm">
                            <div>
                                <label className="block font-black text-gray-500 uppercase text-[10px] mb-1">Valor Final (Y)</label>
                                <input type="number" step="0.01" value={valorFinal} onChange={(e) => setValorFinal(e.target.value)} className="w-full px-3 py-2 bg-white rounded-md border border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 font-mono" />
                            </div>
                            <div>
                                <label className="block font-black text-gray-500 uppercase text-[10px] mb-1">Câmbio Real (R$)</label>
                                <input type="number" step="0.0001" value={taxaCambioDesembaraco} onChange={(e) => setTaxaCambioDesembaraco(e.target.value)} className="w-full px-3 py-2 bg-white rounded-md border border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 font-mono" />
                            </div>
                            <div>
                                <label className="block font-black text-gray-500 uppercase text-[10px] mb-1">Data do Desembaraço</label>
                                <input type="date" value={dataDesembaraco} onChange={(e) => setDataDesembaraco(e.target.value)} className="w-full px-3 py-2 bg-white rounded-md border border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 font-mono" />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 text-sm pt-2">
                            <div className="bg-gray-100 opacity-80 p-3 rounded-lg border border-gray-200">
                                <label className="block font-black text-gray-500 uppercase text-[11px] mb-1">Sinal 30% Já Pago (R$)</label>
                                <input type="number" value={importacao?.valorPago30 || 0} readOnly disabled className="w-full px-3 py-2 bg-gray-100 text-gray-500 cursor-not-allowed rounded-md border border-gray-200 font-mono" />
                            </div>
                            <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
                                <label className="block font-black text-blue-600 uppercase text-[11px] mb-1">Saldo 70% Restante (R$)</label>
                                <input type="number" step="0.01" value={real70Restante} onChange={(e) => setReal70Restante(e.target.value)} className="w-full px-3 py-2 bg-white rounded-md border border-blue-200 shadow-sm focus:ring-blue-500 font-mono text-blue-900" />
                            </div>
                            <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
                                <label className="block font-black text-blue-600 uppercase text-[11px] mb-1">Desembaraço Projetado 50% (R$)</label>
                                <input type="number" step="0.01" value={real50Desembaraco} onChange={(e) => setReal50Desembaraco(e.target.value)} className="w-full px-3 py-2 bg-white rounded-md border border-blue-200 shadow-sm focus:ring-blue-500 font-mono text-blue-900" />
                            </div>
                        </div>
                    </div>

                    {/* BLOCO 2: Financiamento de Entrada (30%) */}
                    <div className="bg-gray-50/80 p-5 rounded-xl border border-gray-200">
                        <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                            <FiCreditCard className="text-indigo-500" /> 2. Financiamento da Entrada (30%)
                        </h3>

                        <label className="flex items-center gap-3 cursor-pointer mb-5">
                            <div className="relative">
                                <input type="checkbox" className="sr-only" checked={possuiFinimp30} onChange={(e) => setPossuiFinimp30(e.target.checked)} />
                                <div className={`block w-14 h-8 rounded-full transition-colors ${possuiFinimp30 ? 'bg-indigo-600' : 'bg-gray-300'}`}></div>
                                <div className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform ${possuiFinimp30 ? 'transform translate-x-6' : ''}`}></div>
                            </div>
                            <span className="font-bold text-indigo-900">Aplicar Finimp na Entrada (30%)?</span>
                        </label>

                        {possuiFinimp30 && (
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-indigo-50 p-4 rounded-xl border border-indigo-100 animate-in slide-in-from-top-4">
                                <div className="md:col-span-2">
                                    <label className="block text-[10px] font-black text-indigo-800 uppercase mb-1">Banco</label>
                                    <input type="text" placeholder="Ex: Itaú" value={bancoFinimp30} onChange={(e) => setBancoFinimp30(e.target.value)} className="w-full px-3 py-2 bg-white border border-indigo-200 rounded-lg focus:ring-2 focus:ring-indigo-500 shadow-sm" />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-[10px] font-black text-indigo-800 uppercase mb-1">Contratação</label>
                                    <input type="date" value={dataContratacaoFinimp30} onChange={(e) => setDataContratacaoFinimp30(e.target.value)} className="w-full px-3 py-2 bg-white border border-indigo-200 rounded-lg font-mono text-sm shadow-sm" />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="flex items-center gap-1 text-[10px] font-black text-indigo-800 uppercase mb-1"><FiPercent /> Juros a.m. (%)</label>
                                    <input type="number" step="0.001" placeholder="Ex: 0.99" value={taxaJurosMensal30} onChange={(e) => setTaxaJurosMensal30(e.target.value)} className="w-full px-3 py-2 bg-white rounded-md border border-indigo-200 focus:ring-indigo-500 focus:border-indigo-500 font-mono" />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="flex items-center gap-1 text-[10px] font-black text-indigo-800 uppercase mb-1"><FiClock /> Prazo (Dias)</label>
                                    <input type="number" placeholder="Ex: 180" value={prazoFinimp30Dias} onChange={(e) => setPrazoFinimp30Dias(e.target.value)} className="w-full px-3 py-2 bg-white rounded-md border border-indigo-200 focus:ring-indigo-500 focus:border-indigo-500 font-mono" />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* BLOCO 3: Financiamento de Saldo (70%) */}
                    <div className="bg-gray-50/80 p-5 rounded-xl border border-gray-200">
                        <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                            <FiCreditCard className="text-orange-500" /> 3. Financiamento do Saldo (70%)
                        </h3>

                        <label className="flex items-center gap-3 cursor-pointer mb-5">
                            <div className="relative">
                                <input type="checkbox" className="sr-only" checked={possuiFinimp70} onChange={(e) => setPossuiFinimp70(e.target.checked)} />
                                <div className={`block w-14 h-8 rounded-full transition-colors ${possuiFinimp70 ? 'bg-orange-500' : 'bg-gray-300'}`}></div>
                                <div className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform ${possuiFinimp70 ? 'transform translate-x-6' : ''}`}></div>
                            </div>
                            <span className="font-bold text-orange-900">Aplicar Finimp no Saldo (70%)?</span>
                        </label>

                        {possuiFinimp70 && (
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-orange-50 p-4 rounded-xl border border-orange-100 animate-in slide-in-from-top-4">
                                <div className="md:col-span-2">
                                    <label className="block text-[10px] font-black text-orange-800 uppercase mb-1">Banco</label>
                                    <input type="text" placeholder="Ex: Santander" value={bancoFinimp70} onChange={(e) => setBancoFinimp70(e.target.value)} className="w-full px-3 py-2 bg-white border border-orange-200 rounded-lg focus:ring-2 focus:ring-orange-500 shadow-sm" />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-[10px] font-black text-orange-800 uppercase mb-1">Contratação</label>
                                    <input type="date" value={dataContratacaoFinimp70} onChange={(e) => setDataContratacaoFinimp70(e.target.value)} className="w-full px-3 py-2 bg-white border border-orange-200 rounded-lg font-mono text-sm shadow-sm" />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="flex items-center gap-1 text-[10px] font-black text-orange-800 uppercase mb-1"><FiPercent /> Juros a.m. (%)</label>
                                    <input type="number" step="0.001" placeholder="Ex: 1.25" value={taxaJurosMensal70} onChange={(e) => setTaxaJurosMensal70(e.target.value)} className="w-full px-3 py-2 bg-white rounded-md border border-orange-200 focus:ring-orange-500 focus:border-orange-500 font-mono" />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="flex items-center gap-1 text-[10px] font-black text-orange-800 uppercase mb-1"><FiClock /> Prazo (Dias)</label>
                                    <input type="number" placeholder="Ex: 360" value={prazoFinimp70Dias} onChange={(e) => setPrazoFinimp70Dias(e.target.value)} className="w-full px-3 py-2 bg-white rounded-md border border-orange-200 focus:ring-orange-500 focus:border-orange-500 font-mono" />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Resumo Consolidado de Juros (se algum finimp estiver habilitado) */}
                    {(possuiFinimp30 || possuiFinimp70) && (
                        <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white p-5 rounded-2xl flex justify-between items-center shadow-xl mb-4 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-5 rounded-full -mr-10 -mt-10 blur-xl"></div>
                            <span className="text-sm font-black uppercase tracking-widest text-gray-400 relative z-10">Total de Juros Estimados:</span>
                            <span className="text-3xl font-black relative z-10 text-emerald-400">
                                {`R$ ${totalJurosEstimados.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            </span>
                        </div>
                    )}

                </div>

                <div className="px-6 py-4 bg-white border-t border-gray-100 flex justify-end gap-3 shrink-0 rounded-b-2xl">
                    <button type="button" onClick={handleClose} className="px-5 py-2 text-gray-500 font-bold hover:bg-gray-100 rounded-xl transition-colors">Cancelar</button>
                    <button 
                        type="button" 
                        onClick={handleConfirm} 
                        disabled={isSubmitting || ((possuiFinimp30 || possuiFinimp70) && isSimulando)}
                        className="px-8 py-3 bg-blue-600 text-white rounded-xl font-black shadow-lg hover:bg-blue-700 active:scale-95 disabled:bg-blue-300 disabled:scale-100 transition-all"
                    >
                        {isSubmitting ? 'Processando...' : 'Confirmar e Encerrar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DesembaracoModal;